import React, { useCallback, useEffect, useRef, useState } from "react";
import StudioCanvas from "./StudioCanvas";
import { Button } from "./StudioControls";

const views = [
  { label: "Front cover", detail: "Scroll to open the cover to the left" },
  { label: "Open flap", detail: "Scroll to unfold the tucked panel to the right" },
  { label: "Inside", detail: "Fully opened · scroll to turn the sheet over" },
  { label: "Outside", detail: "Outside · scroll to fold from the rear" },
  { label: "Rear fold", detail: "One flap closed · scroll to close the remaining flap" },
  { label: "Back cover", detail: "Closed from the rear · scroll back to reopen" },
];
const clamp = (value, max = 1) => Math.max(0, Math.min(max, value));

export default function StudioTrifoldPreview({ doc, content }) {
  const [progress, setProgress] = useState(0);
  const [magnification, setMagnification] = useState(1);
  const [size, setSize] = useState({ width: 1000, height: 700 });
  const stage = useRef(null);
  const touch = useRef(null);
  const panelWidth = doc.pages[0].width / 3;
  const pageHeight = doc.pages[0].height;
  const view = Math.floor(progress);
  const current = views[view];
  const scrub = useCallback((delta) => setProgress(value => clamp(value + delta / 420, 5)), []);

  useEffect(() => {
    const element = stage.current;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    element.parentElement.focus();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    const root = element.closest(".menu-studio") || element.parentElement;
    const wheel = (event) => {
      if (event.target.closest?.(".ms-modal")) return;
      event.preventDefault();
      if (event.ctrlKey || event.metaKey) {
        setMagnification(value => Math.max(.5, Math.min(3, value * Math.exp(-event.deltaY * .008))));
        return;
      }
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      scrub(delta * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? element.clientHeight : 1));
    };
    root.addEventListener("wheel", wheel, { passive: false, capture: true });
    return () => { observer?.disconnect(); root.removeEventListener("wheel", wheel, true); };
  }, [scrub]);

  // Keep the sheet scale fixed so folding never makes the menu jump in size.
  const zoom = magnification * Math.max(.08, Math.min((size.width - 72) / (panelWidth * 3), (size.height - 52) / pageHeight));
  const width = panelWidth * zoom;
  const height = pageHeight * zoom;
  const leftAngle = 180 * (1 - clamp(progress)) + 180 * clamp(progress - 4);
  const rightAngle = -180 * (1 - clamp(progress - 1)) - 180 * clamp(progress - 3);
  const sheetAngle = 180 * clamp(progress - 2);
  // Inside left/center/right reverse to outside right/center/left on the back.
  const face = (pageIndex, panelIndex, back = false) => <div className={`ms-trifold-face${back ? " is-back" : ""}`}
    data-testid={`trifold-panel-${pageIndex}-${panelIndex}`}>
    <div className="ms-trifold-panel-art" style={{ transform: `translateX(${-panelIndex * width}px)` }}>
      <StudioCanvas doc={doc} page={doc.pages[pageIndex]} content={content} zoom={zoom} preview />
    </div>
  </div>;
  const leaf = (panelIndex, angle, side) => <div className={`ms-trifold-leaf is-${side}`} data-testid={`trifold-leaf-${side}`}
    style={{ width, height, left: (panelIndex - 1) * width, transform: `translateZ(${side === "left" ? .6 : .3}px) rotateY(${angle}deg)` }}>
    {face(1, panelIndex)}{face(0, 2 - panelIndex, true)}
  </div>;

  return <section className="ms-menu-preview ms-trifold-preview" aria-label="Takeout trifold preview" tabIndex={0}
    onTouchStart={event => { touch.current = event.touches[0].clientY; }}
    onTouchMove={event => {
      if (touch.current === null) return;
      const y = event.touches[0].clientY;
      scrub(touch.current - y);
      touch.current = y;
    }} onTouchEnd={() => { touch.current = null; }}
    onKeyDown={event => {
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
      event.preventDefault(); event.stopPropagation();
      if (event.key === "Home" || event.key === "End") setProgress(event.key === "Home" ? 0 : 5);
      else setProgress(value => clamp(Math.round(value) + (["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1), 5));
    }}>
    <div className="ms-trifold-stage" ref={stage}>
      <div className="ms-trifold-sheet" data-testid="trifold-sheet" role="group" aria-label={current.detail}
        style={{ width, height, transform: `rotateY(${sheetAngle}deg)` }}>
        <div className="ms-trifold-leaf is-center" style={{ width, height }}>{face(1, 1)}{face(0, 1, true)}</div>
        {leaf(2, rightAngle, "right")}{leaf(0, leftAngle, "left")}
      </div>
    </div>
    <nav className="ms-trifold-navigation" aria-label="Trifold views">
      <Button icon="back" title="Previous fold view" disabled={progress === 0} onClick={() => setProgress(clamp(Math.ceil(progress) - 1, 5))} />
      <div className="ms-trifold-view-tabs">
        {views.map(({ label }, index) => <button key={label} type="button" className={index === view ? "is-active" : ""}
          aria-current={index === view ? "step" : undefined} onClick={() => setProgress(index)}>{label}</button>)}
      </div>
      <Button icon="chevron" title="Next fold view" disabled={progress === 5} onClick={() => setProgress(clamp(Math.floor(progress) + 1, 5))} />
    </nav>
    <div className="ms-trifold-caption" aria-live="polite">{current.detail}</div>
  </section>;
}
